// ./src/components/features/EmailLayouts/EmailLayoutsTable/EmailLayoutsTable.tsx
import React, { useMemo } from "react";
import { Text, Badge, Group } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
  AppButton,
} from "@/components/generic";
import { EmailLayout, isSystemLayout } from "@/utils/api";
import { STATUSES } from "@sdflc/utils";

export interface EmailLayoutsTableProps {
  /** Array of email layouts to display */
  emailLayouts: EmailLayout[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditEmailLayout?: (emailLayout: EmailLayout) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateEmailLayout?: (emailLayout: EmailLayout) => void;

  /** Callback when preview action is clicked */
  onPreviewEmailLayout?: (emailLayout: EmailLayout) => void;

  /** Callback when remove action is clicked */
  onRemoveEmailLayout?: (emailLayout: EmailLayout) => void;

  /** Callback when publish action is clicked */
  onPublishEmailLayout?: (emailLayout: EmailLayout) => void;

  /** ID of email layout currently being removed */
  removingEmailLayoutId?: string | null;

  /** ID of email layout currently being published */
  publishingEmailLayoutId?: string | null;

  /** ID of email layout just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const EmailLayoutsTable: React.FC<EmailLayoutsTableProps> = ({
  emailLayouts,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditEmailLayout,
  onDuplicateEmailLayout,
  onPreviewEmailLayout,
  onRemoveEmailLayout,
  onPublishEmailLayout,
  removingEmailLayoutId,
  publishingEmailLayoutId,
  editedItemId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<EmailLayout>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (value, record) => (
          <div>
            <Group gap="xs">
              <Text fw={500}>{value || "Untitled Layout"}</Text>
              {isSystemLayout(record) && (
                <Badge variant="light" color="blue" size="xs">
                  System
                </Badge>
              )}
            </Group>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 280,
        minWidth: 280,
        align: "right",
        render: (_, record: EmailLayout) => {
          const isSystem = isSystemLayout(record);
          const isRemoving = removingEmailLayoutId === record.id;
          const isPublishing = publishingEmailLayoutId === record.id;
          const isDraft = record.status === STATUSES.DRAFT;
          const canPublish = isDraft && !isSystem && onPublishEmailLayout;

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              {canPublish && (
                <AppButton
                  variant="secondary"
                  size="xs"
                  iconName="Send"
                  onClick={() => onPublishEmailLayout(record)}
                  disabled={isRemoving || isPublishing}
                  loading={isPublishing}
                >
                  Publish
                </AppButton>
              )}
              <RecordActions
                context={record}
                onEditClick={isSystem ? undefined : onEditEmailLayout}
                onRemoveClick={isSystem ? undefined : onRemoveEmailLayout}
                onDuplicateClick={onDuplicateEmailLayout}
                onViewClick={onPreviewEmailLayout}
                removeInProgress={isRemoving}
                editTooltip={
                  isSystem ? "System layouts cannot be edited" : undefined
                }
                removeTooltip={
                  isSystem ? "System layouts cannot be removed" : undefined
                }
              />
            </Group>
          );
        },
      },
    ],
    [
      onEditEmailLayout,
      onDuplicateEmailLayout,
      onPreviewEmailLayout,
      onRemoveEmailLayout,
      onPublishEmailLayout,
      removingEmailLayoutId,
      publishingEmailLayoutId,
    ]
  );

  return (
    <AppTable
      data={emailLayouts}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(emailLayout) => emailLayout?.id || ""}
      highlightRow={(emailLayout) => emailLayout?.id === editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
